import { useState, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Upload, ArrowRight, ArrowLeft, AlertCircle, CheckCircle2, FileSpreadsheet, Loader2,
} from 'lucide-react';
import { api } from '../../lib/api.ts';
import { extractError } from '../../lib/types.ts';
import { Modal, ModalFooter } from '../ui/Modal.tsx';
import { Button } from '../ui/Button.tsx';
import { Badge } from '../ui/Badge.tsx';
import { useToast } from '../ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PreviewRow {
  rowNumber: number;
  raw: Record<string, string>;
  mapped: Record<string, string>;
  errors: string[];
  isValid: boolean;
}

interface PreviewResult {
  detectedMapping: Record<string, string>;
  availableFields: string[];
  csvColumns: string[];
  totalRows: number;
  previewRows: PreviewRow[];
  validationSummary: { validRows: number; invalidRows: number };
}

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ rowNumber: number; email?: string; error: string }>;
  totalProcessed: number;
  segmentAssigned?: number;
  durationMs: number;
}

interface Segment {
  id: string;
  name: string;
  type: string;
}

type Step = 'upload' | 'mapping' | 'preview' | 'result';

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CustomerImportWizard({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [upsertBehavior, setUpsertBehavior] = useState<string>('create_and_update');
  const [segmentId, setSegmentId] = useState('');
  const [skipInvalid, setSkipInvalid] = useState(true);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Fetch static segments for the optional segment picker
  const { data: allSegments = [] } = useQuery<Segment[]>({
    queryKey: ['segments'],
    queryFn: () => api.get('/api/v1/segments').then((r) => r.data),
    enabled: open,
  });
  const staticSegments = allSegments.filter((s) => s.type === 'static');

  // Reset state on close/open
  const handleClose = () => {
    setStep('upload');
    setCsvText('');
    setFileName('');
    setRowCount(0);
    setMapping({});
    setPreview(null);
    setImportResult(null);
    setUpsertBehavior('create_and_update');
    setSegmentId('');
    setSkipInvalid(true);
    onClose();
  };

  // ── Step 1: Upload ──────────────────────────────────────────────────────────

  const handleFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvText(text);
      setFileName(file.name);
      const lines = text.split('\n').filter((l) => l.trim());
      setRowCount(Math.max(0, lines.length - 1)); // minus header
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  // ── Step 2: Mapping (uses preview endpoint) ─────────────────────────────────

  const previewMutation = useMutation({
    mutationFn: (payload: { csv: string; fieldMapping?: Record<string, string> | null }) =>
      api.post('/api/v1/customers/import/preview', payload).then((r) => r.data as PreviewResult),
    onSuccess: (data) => {
      setPreview(data);
      if (step === 'upload') {
        // First preview call — auto-detect mapping
        setMapping(data.detectedMapping);
        setStep('mapping');
      } else {
        // Re-preview after mapping change
        setStep('preview');
      }
    },
    onError: (err) => toast.error(extractError(err, 'Failed to parse CSV')),
  });

  const goToMapping = () => {
    previewMutation.mutate({ csv: csvText });
  };

  const goToPreview = () => {
    previewMutation.mutate({ csv: csvText, fieldMapping: mapping });
  };

  // ── Step 4: Execute ─────────────────────────────────────────────────────────

  const executeMutation = useMutation({
    mutationFn: () =>
      api
        .post('/api/v1/customers/import/execute', {
          csv: csvText,
          fieldMapping: mapping,
          options: {
            upsertBehavior,
            segmentId: segmentId || undefined,
            skipInvalidRows: skipInvalid,
          },
        })
        .then((r) => r.data as ImportResult),
    onSuccess: (data) => {
      setImportResult(data);
      setStep('result');
      qc.invalidateQueries({ queryKey: ['customers'] });
      if (segmentId) {
        qc.invalidateQueries({ queryKey: ['segment-members', segmentId] });
        qc.invalidateQueries({ queryKey: ['segment', segmentId] });
      }
    },
    onError: (err) => toast.error(extractError(err, 'Import failed')),
  });

  // ── Mapping change handler ──────────────────────────────────────────────────

  const updateMapping = (csvCol: string, field: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (field === '__skip__') {
        delete next[csvCol];
      } else {
        next[csvCol] = field;
      }
      return next;
    });
  };

  // Check if email is mapped
  const emailMapped = Object.values(mapping).includes('email');

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal open={open} onClose={handleClose} title="Import Customers from CSV" size="lg">
      {step === 'upload' && (
        <div className="space-y-4">
          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-zinc-200 hover:border-zinc-400 rounded-xl p-8 text-center cursor-pointer transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
            {csvText ? (
              <div className="space-y-2">
                <FileSpreadsheet size={28} className="text-emerald-500 mx-auto" />
                <p className="text-sm font-medium text-zinc-800">{fileName}</p>
                <p className="text-xs text-zinc-400">
                  ~{rowCount.toLocaleString()} rows · Click to change file
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload size={28} className="text-zinc-300 mx-auto" />
                <p className="text-sm text-zinc-500">
                  Drag & drop a CSV file here, or click to browse
                </p>
                <p className="text-xs text-zinc-400">Max 10 MB · UTF-8 encoding</p>
              </div>
            )}
          </div>

          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!csvText}
              loading={previewMutation.isPending}
              onClick={goToMapping}
            >
              Continue <ArrowRight size={12} className="ml-1" />
            </Button>
          </ModalFooter>
        </div>
      )}

      {step === 'mapping' && preview && (
        <div className="space-y-4">
          <p className="text-xs text-zinc-500">
            Map CSV columns to customer fields. At least <strong>email</strong> is required.
          </p>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {preview.csvColumns.map((col) => (
              <div key={col} className="flex items-center gap-3">
                <span className="text-xs text-zinc-600 font-mono w-40 truncate shrink-0" title={col}>
                  {col}
                </span>
                <ArrowRight size={12} className="text-zinc-300 shrink-0" />
                <select
                  value={mapping[col] ?? '__skip__'}
                  onChange={(e) => updateMapping(col, e.target.value)}
                  className="flex-1 text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:border-zinc-400 focus:outline-none"
                >
                  <option value="__skip__">— Skip this column —</option>
                  {preview.availableFields.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          {!emailMapped && (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              <AlertCircle size={14} /> You must map at least one column to "email"
            </div>
          )}

          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setStep('upload')}>
              <ArrowLeft size={12} className="mr-1" /> Back
            </Button>
            <Button
              size="sm"
              disabled={!emailMapped}
              loading={previewMutation.isPending}
              onClick={goToPreview}
            >
              Preview <ArrowRight size={12} className="ml-1" />
            </Button>
          </ModalFooter>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-4">
          {/* Validation summary */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs text-emerald-600">
              <CheckCircle2 size={13} />
              {preview.validationSummary.validRows.toLocaleString()} valid
            </div>
            {preview.validationSummary.invalidRows > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-red-500">
                <AlertCircle size={13} />
                {preview.validationSummary.invalidRows.toLocaleString()} invalid
              </div>
            )}
            <span className="text-xs text-zinc-400">
              Total: {preview.totalRows.toLocaleString()} rows
            </span>
          </div>

          {/* Preview table */}
          <div className="overflow-x-auto max-h-48 border border-zinc-200 rounded-lg">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-100">
                  <th className="px-2 py-1.5 text-left text-zinc-400 font-medium">#</th>
                  {Object.values(mapping).map((field) => (
                    <th key={field} className="px-2 py-1.5 text-left text-zinc-400 font-medium">
                      {field}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-left text-zinc-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-50">
                {preview.previewRows.map((row) => (
                  <tr key={row.rowNumber} className={row.isValid ? '' : 'bg-red-50/40'}>
                    <td className="px-2 py-1.5 text-zinc-400">{row.rowNumber}</td>
                    {Object.values(mapping).map((field) => (
                      <td key={field} className="px-2 py-1.5 text-zinc-700 max-w-[120px] truncate">
                        {row.mapped[field] || <span className="text-zinc-300">—</span>}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      {row.isValid ? (
                        <Badge variant="success">OK</Badge>
                      ) : (
                        <span className="text-red-500" title={row.errors.join('; ')}>
                          {row.errors[0]}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Options */}
          <div className="space-y-3 border-t border-zinc-100 pt-3">
            <div className="flex items-center gap-3">
              <label className="text-xs text-zinc-500 w-28 shrink-0">Behavior</label>
              <select
                value={upsertBehavior}
                onChange={(e) => setUpsertBehavior(e.target.value)}
                className="flex-1 text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:border-zinc-400 focus:outline-none"
              >
                <option value="create_and_update">Create new + update existing</option>
                <option value="create_only">Create only (skip existing)</option>
                <option value="update_only">Update only (skip new)</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-xs text-zinc-500 w-28 shrink-0">Add to segment</label>
              <select
                value={segmentId}
                onChange={(e) => setSegmentId(e.target.value)}
                className="flex-1 text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white focus:border-zinc-400 focus:outline-none"
              >
                <option value="">— None —</option>
                {staticSegments.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={skipInvalid}
                onChange={(e) => setSkipInvalid(e.target.checked)}
                className="rounded border-zinc-300"
              />
              Skip invalid rows (otherwise import will stop at first error)
            </label>
          </div>

          <ModalFooter>
            <Button variant="ghost" size="sm" onClick={() => setStep('mapping')}>
              <ArrowLeft size={12} className="mr-1" /> Back
            </Button>
            <Button
              size="sm"
              loading={executeMutation.isPending}
              onClick={() => executeMutation.mutate()}
            >
              {executeMutation.isPending ? (
                <>
                  <Loader2 size={12} className="mr-1 animate-spin" /> Importing…
                </>
              ) : (
                <>Import {preview.validationSummary.validRows.toLocaleString()} rows</>
              )}
            </Button>
          </ModalFooter>
        </div>
      )}

      {step === 'result' && importResult && (
        <div className="space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
            <CheckCircle2 size={28} className="text-emerald-500 mx-auto mb-2" />
            <p className="text-sm font-semibold text-zinc-800">Import completed</p>
            <p className="text-xs text-zinc-500 mt-1">
              {(importResult.durationMs / 1000).toFixed(1)}s
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Stat label="Created" value={importResult.created} color="emerald" />
            <Stat label="Updated" value={importResult.updated} color="blue" />
            <Stat label="Skipped" value={importResult.skipped} color="zinc" />
          </div>

          {importResult.segmentAssigned != null && (
            <p className="text-xs text-zinc-500 text-center">
              {importResult.segmentAssigned.toLocaleString()} customers assigned to segment
            </p>
          )}

          {importResult.errors.length > 0 && (
            <details className="text-xs">
              <summary className="text-red-500 cursor-pointer font-medium">
                {importResult.errors.length} errors (click to expand)
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3 space-y-1">
                {importResult.errors.map((e, i) => (
                  <p key={i} className="text-red-600">
                    Row {e.rowNumber}{e.email ? ` (${e.email})` : ''}: {e.error}
                  </p>
                ))}
              </div>
            </details>
          )}

          <ModalFooter>
            <Button size="sm" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    emerald: 'text-emerald-600 bg-emerald-50',
    blue: 'text-blue-600 bg-blue-50',
    zinc: 'text-zinc-600 bg-zinc-100',
  };
  return (
    <div className={`rounded-lg p-3 text-center ${colors[color] ?? colors.zinc}`}>
      <p className="text-lg font-bold tabular-nums">{value.toLocaleString()}</p>
      <p className="text-[11px] mt-0.5">{label}</p>
    </div>
  );
}
